import { useState, useEffect } from "react"
import { router } from "@inertiajs/react"
import {
  Dialog,
  DialogContent,
  DialogBody,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { Label } from "@/components/ui/label"
import { Loader2 } from "lucide-react"
import type { CourrierTemplate } from "@/types"

type Props = {
  open: boolean
  onOpenChange: (open: boolean) => void
  template: CourrierTemplate | null
}

export function CourrierTemplateFormDialog({ open, onOpenChange, template }: Props) {
  const isEditing = !!template
  const [processing, setProcessing] = useState(false)

  const [title, setTitle] = useState("")
  const [pdfmonkeyTemplateId, setPdfmonkeyTemplateId] = useState("")
  const [body, setBody] = useState("")
  const [bgImageUrl, setBgImageUrl] = useState("")

  useEffect(() => {
    if (open) {
      setTitle(template?.title || "")
      setPdfmonkeyTemplateId(template?.pdfmonkey_template_id || "")
      setBody(template?.body || "")
      setBgImageUrl(template?.bg_image_url || "")
    }
  }, [open, template])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setProcessing(true)

    const data = {
      title,
      pdfmonkey_template_id: pdfmonkeyTemplateId,
      body: body || null,
      bg_image_url: bgImageUrl || null,
    }

    if (isEditing) {
      router.put(`/courrier_templates/${template.id}`, data, {
        onSuccess: () => {
          setProcessing(false)
          onOpenChange(false)
        },
        onError: () => setProcessing(false),
      })
    } else {
      router.post("/courrier_templates", data, {
        onSuccess: () => {
          setProcessing(false)
          onOpenChange(false)
        },
        onError: () => setProcessing(false),
      })
    }
  }

  const canSubmit = title.trim() && pdfmonkeyTemplateId.trim()

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {isEditing ? "Modifier le modèle" : "Nouveau modèle de courrier"}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit}>
          <DialogBody className="space-y-4">
            <div>
              <Label htmlFor="ct-title">Titre *</Label>
              <Input
                id="ct-title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Ex: Courrier de prospection"
                className="mt-1"
              />
            </div>

            <div>
              <Label htmlFor="ct-template-id">ID template PDFMonkey *</Label>
              <Input
                id="ct-template-id"
                value={pdfmonkeyTemplateId}
                onChange={(e) => setPdfmonkeyTemplateId(e.target.value)}
                placeholder="Ex: abc123-def456-..."
                className="mt-1"
              />
            </div>

            <div>
              <Label htmlFor="ct-body">Corps du message</Label>
              <Textarea
                id="ct-body"
                value={body}
                onChange={(e) => setBody(e.target.value)}
                placeholder="Texte du courrier avec variables : %owner.fullName%, %project.address%, etc."
                rows={6}
                className="mt-1"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Variables : %owner.fullName%, %owner.gender%, %project.name%, %project.address%, %user.first_name%, %user.last_name%
              </p>
            </div>

            <div>
              <Label htmlFor="ct-bg-url">URL image de fond</Label>
              <Input
                id="ct-bg-url"
                value={bgImageUrl}
                onChange={(e) => setBgImageUrl(e.target.value)}
                placeholder="https://..."
                className="mt-1"
              />
            </div>
          </DialogBody>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Annuler
            </Button>
            <Button type="submit" disabled={processing || !canSubmit}>
              {processing && <Loader2 className="size-4 animate-spin" />}
              {isEditing ? "Enregistrer" : "Créer"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
